'use client';

import { Box, useMediaQuery, useTheme } from '@mui/material';

import { AbgTypography, formatMoney, mergeSx } from '@abg/kit-base';

import { usePricingDisplayConfig } from '../../../hooks';
import { useTranslationContext } from '../../../shared/context';
import { useIsFlexPayEnabled } from '../../../shared/hooks/use-is-flex-pay-enabled';
import { AncillariesI18nKeys } from '../../../shared/types';
import { userStoreSelector } from '../../../store';
import { getPriceLabelSuffix } from '../utils/utils';

import {
  bookAgainStyling,
  containerStyling,
  footerContainerStyling,
  oldPriceStyling,
  recommendedStyling,
} from './AncillariesBundleBody.styling';
import { AncillariesBundleItems } from './AncillariesBundleItems';
import { RECOMMENDED_LABEL_TEST_ID } from './constants';
import { AncillariesBundleBodyProps } from './types';

export const AncillariesBundleBody = ({
  styleParts = {},
  items,
  oldPrice,
  price,
  bookAgain,
  recommended,
  currencyCode,
}: AncillariesBundleBodyProps) => {
  const isAuthenticated = userStoreSelector.use.isAuthenticated();

  const { t, i18nKeys } = useTranslationContext<AncillariesI18nKeys>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isFlexPayEnabled = useIsFlexPayEnabled();
  const { showDailyRate } = usePricingDisplayConfig();

  const shouldRenderRecommendedLabel = isMobile && recommended && !bookAgain;

  // NOTE: here it will be hard coded since it depends on user select,
  // and this select on vehicle pages is available only in case
  // location is not usa, in case use it will be empty.
  // So we assume it is USD
  const currency = currencyCode ?? 'USD';
  const priceLabelSuffix = getPriceLabelSuffix(showDailyRate, t(i18nKeys.day));

  return (
    <Box sx={containerStyling} data-testid="ancillaries-bundle-body">
      <AncillariesBundleItems items={items} styleParts={styleParts} />

      <Box sx={footerContainerStyling}>
        <Box>
          {!!oldPrice && (
            <AbgTypography
              sx={mergeSx(oldPriceStyling, styleParts.oldPrice)}
              data-testid="ancillaries-bundle-strikethrough-price"
            >
              {formatMoney(Number(oldPrice ?? 0), { currency })}
              {priceLabelSuffix}
            </AbgTypography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <AbgTypography
              sx={{ ...styleParts.price }}
              data-testid="ancillaries-bundle-price"
              className={isFlexPayEnabled ? 'fxp-ancillary' : ''}
            >
              {formatMoney(Number(price ?? 0), { currency })}
            </AbgTypography>
            {priceLabelSuffix && (
              <AbgTypography
                sx={{ ...styleParts.priceLabel }}
                data-testid="ancillaries-bundle-price-label"
              >
                {priceLabelSuffix}
              </AbgTypography>
            )}
          </Box>
        </Box>

        {isAuthenticated && bookAgain && (
          <Box
            sx={mergeSx(bookAgainStyling, styleParts.bookAgain)}
            data-testid="ancillaries-bundle-bookAgain"
          >
            {t(i18nKeys.bookAgainLabel)}
          </Box>
        )}

        {shouldRenderRecommendedLabel && (
          <Box
            sx={mergeSx(recommendedStyling, styleParts.recommended)}
            data-testid={RECOMMENDED_LABEL_TEST_ID}
          >
            {t(i18nKeys.recommendedLabel)}
          </Box>
        )}
      </Box>
    </Box>
  );
};
